import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { logDebug, logInfo } from '../../logging/loggingService';
import { getKeycloak, initKeycloak, isKeycloakMode } from '../../auth/keycloakClient';
import { getCurrentUser } from '../../api';

const AUTH_FLAG_KEY = 'isAuthenticated';
const USER_STORAGE_KEY = 'authUser';

const detectInitialPersistence = () => {
    try {
        if (localStorage.getItem(USER_STORAGE_KEY) || localStorage.getItem(AUTH_FLAG_KEY) === 'true') {
            return 'local';
        }
    } catch {}
    try {
        if (sessionStorage.getItem(USER_STORAGE_KEY) || sessionStorage.getItem(AUTH_FLAG_KEY) === 'true') {
            return 'session';
        }
    } catch {}
    return 'session';
};

const readStoredUser = () => {
    try {
        const raw = localStorage.getItem(USER_STORAGE_KEY) || sessionStorage.getItem(USER_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        return JSON.parse(raw);
    } catch (error) {
        logDebug('Failed to parse stored user', { component: 'AuthContext', error });
        return null;
    }
};

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(() => {
        const storedValue =
            localStorage.getItem(AUTH_FLAG_KEY) || sessionStorage.getItem(AUTH_FLAG_KEY);
        logDebug('Initial isAuthenticated value resolved from storage', {
            component: 'AuthContext',
            storedValue,
        });
        return storedValue === 'true';
    });

    const [persistence, setPersistence] = useState(detectInitialPersistence);
    const [user, setUser] = useState(readStoredUser);

    useEffect(() => {
        if (!isKeycloakMode()) {
            return;
        }
        (async () => {
            try {
                const { authenticated } = await initKeycloak();
                if (!authenticated) {
                    setIsAuthenticated(false);
                    setUser(null);
                    return;
                }
                setIsAuthenticated(true);
                // Pull the domain user from backend (includes permissions) once authenticated.
                const me = await getCurrentUser();
                if (me && me.data) {
                    setUser(me.data);
                } else {
                    const kc = getKeycloak();
                    setUser({ username: kc?.tokenParsed?.preferred_username || kc?.tokenParsed?.email || 'user' });
                }
            } catch (e) {
                setIsAuthenticated(false);
                setUser(null);
            }
        })();
    }, []);

    const applyPersistence = useCallback((remember, nextUser) => {
        const storage = remember ? localStorage : sessionStorage;
        const altStorage = remember ? sessionStorage : localStorage;

        try {
            storage.setItem(AUTH_FLAG_KEY, 'true');
        } catch {}
        try {
            altStorage.removeItem(AUTH_FLAG_KEY);
        } catch {}

        try {
            if (nextUser) {
                storage.setItem(USER_STORAGE_KEY, JSON.stringify(nextUser));
            } else {
                storage.removeItem(USER_STORAGE_KEY);
            }
        } catch {}

        try {
            altStorage.removeItem(USER_STORAGE_KEY);
        } catch {}

        setPersistence(remember ? 'local' : 'session');
    }, []);

    const syncUserToPersistence = useCallback((nextUser) => {
        const remember = persistence === 'local';
        const storage = remember ? localStorage : sessionStorage;
        const altStorage = remember ? sessionStorage : localStorage;

        try {
            if (nextUser) {
                storage.setItem(USER_STORAGE_KEY, JSON.stringify(nextUser));
                storage.setItem(AUTH_FLAG_KEY, 'true');
            } else {
                storage.removeItem(USER_STORAGE_KEY);
                storage.removeItem(AUTH_FLAG_KEY);
            }
        } catch {}

        try {
            altStorage.removeItem(USER_STORAGE_KEY);
            if (!remember) {
                localStorage.removeItem(AUTH_FLAG_KEY);
            } else {
                sessionStorage.removeItem(AUTH_FLAG_KEY);
            }
        } catch {}
    }, [persistence]);

    const logout = useCallback((options = {}) => {
        logInfo('Logout invoked', { component: 'AuthContext' });
        const { keycloakLogout = true } = options || {};
        if (isKeycloakMode()) {
            const kc = getKeycloak();
            if (kc && keycloakLogout) {
                // best-effort
                kc.logout({ redirectUri: window.location.origin + '/' });
            }
        }
        setIsAuthenticated(false);
        setUser(null);
        try { localStorage.removeItem(AUTH_FLAG_KEY); } catch {}
        try { sessionStorage.removeItem(AUTH_FLAG_KEY); } catch {}
        try { localStorage.removeItem(USER_STORAGE_KEY); } catch {}
        try { sessionStorage.removeItem(USER_STORAGE_KEY); } catch {}
        try { localStorage.removeItem('authToken'); } catch {}
        try { sessionStorage.removeItem('authToken'); } catch {}
        try { localStorage.removeItem('authRefreshToken'); } catch {}
        try { sessionStorage.removeItem('authRefreshToken'); } catch {}
        setPersistence('session');
    }, []);

    const login = useCallback(async (userData, options = {}) => {
        if (isKeycloakMode()) {
            const kc = getKeycloak();
            if (kc) {
                try {
                    // keycloak-js needs init() to populate endpoints (authorize, token, ...).
                    // This does not trigger redirects because we don't set onLoad in init options.
                    await initKeycloak();
                } catch (e) {
                    logInfo('Keycloak pre-login init failed', { component: 'AuthContext', error: e });
                }
                try {
                    kc.login({ redirectUri: window.location.origin + '/loginSuccess' });
                } catch (e) {
                    logInfo('Keycloak login redirect failed', { component: 'AuthContext', error: e });
                }
            }
            return;
        }
        const remember = options.remember !== false;
        logInfo('Login invoked', {
            component: 'AuthContext',
            remember,
            userId: userData?.id,
        });
        setIsAuthenticated(true);
        setUser(userData ?? null);
        if (userData) {
            applyPersistence(remember, userData);
        } else {
            applyPersistence(remember, null);
        }
    }, [applyPersistence]);

    const updateUserDetails = useCallback((userData, options = {}) => {
        const remember = options.remember ?? (persistence === 'local');
        if (!userData) {
            logout();
            return;
        }
        setIsAuthenticated(true);
        setUser(userData);
        if (remember === (persistence === 'local')) {
            syncUserToPersistence(userData);
        } else {
            applyPersistence(remember, userData);
        }
    }, [applyPersistence, logout, persistence, syncUserToPersistence]);

    const hasPermission = useCallback((permission) => {
        if (!permission) {
            return false;
        }
        const granted = user?.permissions;
        return Array.isArray(granted) && granted.includes(permission);
    }, [user]);

    const hasAnyPermission = useCallback((permissions = []) => {
        if (!Array.isArray(permissions)) {
            return false;
        }
        return permissions.some((permission) => hasPermission(permission));
    }, [hasPermission]);

    const contextValue = {
        isAuthenticated,
        user,
        login,
        logout,
        updateUser: updateUserDetails,
        hasPermission,
        hasAnyPermission,
        persistence,
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};
